import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PasswordInput from '../components/PasswordInput';
import CaptchaWidget from '../components/CaptchaWidget';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { analytics } from '../lib/analytics';
import MoonLogo from '../components/MoonLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Register = () => {
  const navigate = useNavigate();
  const { register, isAuthenticated, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [captchaSiteKey, setCaptchaSiteKey] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/terminal');
    }
  }, [isAuthenticated, loading, navigate]);

  useEffect(() => {
    analytics.signupStarted();
  }, []);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/auth/config`)
      .then(({ data }) => setCaptchaSiteKey(data?.captcha_site_key || null))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 10) {
      setError('Password must be at least 10 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (captchaSiteKey && !captchaToken) {
      setError('Please complete the captcha');
      return;
    }

    setIsSubmitting(true);
    const result = await register(email, password, name, captchaToken);

    if (result.success) {
      analytics.signupCompleted();
      navigate('/terminal');
    } else {
      setError(result.error);
    }

    setIsSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#06070d] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#f2e2a6]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#06070d] via-[#0b0e1a] to-[#06070d] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2">
            <MoonLogo size={40} />
            <span className="text-white font-semibold text-xl tracking-wide">moon<span className="text-[#f2e2a6]">.land</span></span>
          </Link>
        </div>

        {/* Register Card */}
        <div className="bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
          <h1 className="text-2xl font-bold text-white mb-2 text-center">Create an account</h1>
          <p className="text-gray-400 text-center mb-8">Get started with moon.land</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm" data-testid="register-error">
                {error}
              </div>
            )}

            <div>
              <label className="block text-gray-400 text-sm mb-2">Name (optional)</label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                data-testid="register-name"
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
              />
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-2">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                data-testid="register-email"
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
              />
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-2">Password</label>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={10}
                data-testid="register-password"
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
              />
              <p className="text-gray-500 text-xs mt-1">At least 10 characters</p>
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-2">Confirm password</label>
              <PasswordInput
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="register-confirm-password"
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
              />
            </div>

            {captchaSiteKey && (
              <CaptchaWidget
                siteKey={captchaSiteKey}
                onVerify={setCaptchaToken}
                onExpire={() => setCaptchaToken(null)}
              />
            )}

            <Button
              type="submit"
              disabled={isSubmitting}
              data-testid="register-submit"
              className="w-full bg-[#f2e2a6] text-black hover:bg-[#f2e2a6]/90 font-semibold py-6"
            >
              {isSubmitting ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-400">
              Already have an account?{' '}
              <Link to="/login" className="text-[#f2e2a6] hover:underline" data-testid="goto-login">
                Sign in
              </Link>
            </p>
          </div>
        </div>

        {/* Back to home */}
        <div className="text-center mt-6">
          <Link to="/" className="text-gray-500 hover:text-white text-sm">
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Register;
